import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/enterprise2/MarketingLayout";
import { HairlineList } from "../../components/enterprise2/HairlineList";
import { PullQuote } from "../../components/enterprise2/EditorialPolish";
import { Reveal } from "../../components/enterprise2/Reveal";
import { SubNav } from "../../components/enterprise2/SubNav";

const MODELS = [
  { key: "electron", name: "Electron", role: "The edge", desc: "The light, fast part of the atom, and the layer through which it meets everything else. Electron is the on-device model closest to your people, handling the high-volume everyday work in the branch, on the shop floor and on the machine." },
  { key: "proton", name: "Proton", role: "The enterprise workhorse", desc: "The model that does the defining work of the business: structured extraction, document understanding and workflow automation. Proton determines what the organisation actually is, operationally, day after day." },
  { key: "neutron", name: "Neutron", role: "The librarian", desc: "The retrieval and embeddings model. Neutron grounds the whole system in your own private data, so every answer traces back to a record you hold rather than a guess the model made." },
  { key: "nucleus", name: "Nucleus", role: "The core", desc: "The flagship model at the centre. Nucleus carries the heaviest reasoning, deep analysis and coding, the core that the rest of the organisation's intelligence orbits." },
];

const SUBNAV = [
  { id: "atom-what", label: "What Atom is" },
  { id: "atom-family", label: "The four models" },
  { id: "atom-trained", label: "How it is trained" },
  { id: "atom-stack", label: "In the stack" },
];

const Idx = ({ n, label }) => (
  <div className="e2-idx"><span className="e2-idx-num">{n}</span><span className="e2-idx-rule" /><span className="e2-idx-label">{label}</span></div>
);

// Atom: the intelligence layer of the sovereign stack. Framing drawn from the Crowther brand documentation.
export default function Atom() {
  return (
    <MarketingLayout active="solution" title="Atom | Sovereign, specialist intelligence, owned in-country" description="Atom is the specialist model family at the floor of the Crowther stack: Electron, Proton, Neutron and Nucleus, trained for your work and hosted where your data lives.">
      <section className="e2-hero-banner m2-page-hero" data-testid="e2-atom-hero">
        <div className="e2-hero-banner-grid">
          <div className="e2-hero-banner-lead">
            <div className="e2-eyebrow">Atom</div>
            <h1 className="e2-hero-banner-title" data-testid="e2-atom-title">The basic building block of your organisation&rsquo;s intelligence.</h1>
            <p className="e2-hero-banner-subtitle">Sovereign, specialist artificial intelligence, owned in-country. Four models, trained for your work, hosted where your data already lives.</p>
            <div className="e2-hero-banner-actions">
              <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-atom-cta">Get Your Firm&rsquo;s Free Customised AI Roadmap</Link>
              <Link to="/enterprise-2/solution" className="e2-btn-link" data-testid="e2-atom-back">Back to What We Build
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden><path d="M3 8h9.5M8.5 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SubNav items={SUBNAV} testId="e2-atom-subnav" />

      <Reveal as="section" id="atom-what" className="e2-shell e2-section" data-testid="e2-atom-what">
        <Idx n="01" label="What Atom is" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Not a tool bolted onto the side of the business. The floor it stands on.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">Atom is the intelligence layer of the Crowther stack: the elemental, irreducible unit the rest of the organisation is composed from. An enterprise running on Atom has no layer beneath it that does the thinking. Atom is the floor.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>Each model is lean, post-trained for its job, and empty of everything it does not need. That is what lets it run on your own infrastructure rather than in someone else&rsquo;s cloud, and what lets a regulated institution answer, precisely, where its intelligence lives and who holds the keys.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>The mark says the same thing in a picture: four fig leaves as orbits around a nucleus, so it reads as both a fig and an atom. The fig is a partnership. So is the model.</p>
          </div>
        </div>
        <PullQuote mark="§">Sovereign, specialist intelligence, owned in-country.</PullQuote>
      </Reveal>

      <Reveal as="section" id="atom-family" className="e2-shell e2-section" data-testid="e2-atom-family">
        <Idx n="02" label="The four models" />
        <div className="e2-lede">
          <h2 className="e2-h-l" style={{ maxWidth: "22ch" }}>A nucleus, and the electrons that orbit it.</h2>
          <p className="e2-body e2-body-lg" style={{ maxWidth: "62ch", marginTop: 18 }}>The architecture mirrors the name. Four models, each with one job, arranged from the fast edge to the reasoning core.</p>
        </div>
        <div className="e2-fig-cap code" style={{ marginTop: 40 }}>FIG.1 &nbsp;·&nbsp; The Atom family, edge to core</div>
        <HairlineList rows={MODELS.map((m) => ({ testId: `e2-atom-model-${m.key}`, name: <><span className="e2-idx-num" style={{ marginRight: 14 }}>{m.role}</span>{m.name}</>, desc: m.desc }))} />
      </Reveal>

      <Reveal as="section" id="atom-trained" className="e2-shell e2-section" data-testid="e2-atom-trained">
        <Idx n="03" label="How it is trained" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Thinking locally. Trained locally.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">Atom models are pre-trained on original data and post-trained on open-source foundations, then hosted locally. Global frontier models were built for the average of the internet. Atom is built for the specific: your sector, your language, your regulator, your records.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>Because the model and the data stay on ground you control, the model keeps improving on your work without your work ever leaving the building. Access is deny by default; you grant it, and you can revoke it in a moment.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>Every deployment is held to one design constraint: JQ greater than 1.0. Atom must elevate more human work than it displaces, and we measure it.</p>
          </div>
        </div>
      </Reveal>

      <Reveal as="section" id="atom-stack" className="e2-shell e2-section" data-testid="e2-atom-stack">
        <Idx n="04" label="In the stack" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">The engine beneath Foundation, served by Crowther Cloud.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">Atom is the engine: where the work runs, generating, retrieving and reasoning. Foundation sits above it, resolving your records into the single true entity behind them and governing what the models may see. Crowther Cloud provisions and serves it all in-country, and Realloc measures what the intelligence does for the people who use it.</p>
            <div className="e2-arg-facts" style={{ marginTop: 28 }}>
              {[["Foundation", "The governed platform institutions build on, above Atom."], ["Crowther Cloud", "Provisioning, serving and deployment, in-country, under your regulator's authority."], ["Realloc", "The workforce platform that measures what intelligence elevates."]].map(([n, d], i) => (
                <div key={n} className="e2-arg-fact" data-testid={`e2-atom-stack-${i + 1}`}>
                  <div className="e2-arg-fact-n">0{i + 1}</div>
                  <div><div className="e2-arg-fact-b" style={{ fontSize: 22, marginBottom: 8 }}>{n}</div><p className="e2-body" style={{ margin: 0 }}>{d}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      <section className="e2-cband" data-testid="e2-atom-close" style={{ marginTop: 40 }}>
        <div className="e2-cband-statement">
          <div className="e2-cband-statement-inner">
            <div className="e2-cband-eyebrow" style={{ color: "#6AA1A3" }}>Human insight, amplified.</div>
            <h2 className="e2-cband-statement-title">Access to intelligence is yours.</h2>
            <p className="e2-cband-statement-sub">See where Atom pays for itself first in your firm. Thirteen questions, five minutes, a plan in your inbox.</p>
            <div className="e2-cband-statement-actions">
              <Link to="/plan-2" className="e2-cband-btn e2-cband-btn-primary" data-testid="e2-atom-close-cta">Get Your Firm&rsquo;s Free Customised AI Roadmap</Link>
              <Link to="/enterprise-2/contact" className="e2-cband-btn e2-cband-btn-ghost" data-testid="e2-atom-close-contact">Or talk to us directly</Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
